import { useState, useEffect } from 'react';

import { IconHash, IconStar, IconCategory } from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import { Switch } from '@/components/Switch';
import { LessonStageConfig, ContentStatusConfig } from '@/constants/constant';
import { EContentStatus } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import { getContentDetails } from '@/service/contentService';
import useCommonStore from '@/store/useCommonStore';
import { IContentGameData } from '@/types';
import { formatSecondsToDuration } from '@/utils';

// ─── Types ───────────────────────────────────────────────────────────────────

interface IGameDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  gameId: string | null;
}

// ─── Component ───────────────────────────────────────────────────────────────

const GameDetailsModal = ({ setIsOpen, gameId }: IGameDetailsModalProps) => {
  const { formatText } = useFormatText();
  const { setLoadingModal } = useCommonStore();
  const [game, setGame] = useState<IContentGameData | null>(null);

  useEffect(() => {
    if (!gameId) return;

    const fetchDetails = async () => {
      setLoadingModal(true);
      try {
        const res = await getContentDetails(gameId);
        if (res?.status && res?.data) {
          setGame(res.data);
        }
      } catch (error) {
        console.error('Error fetching game details:', error);
      } finally {
        setLoadingModal(false);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  const stageConfig = game?.category ? LessonStageConfig[game?.category] : null;
  const statusConfig = game?.status
    ? ContentStatusConfig[game?.status as keyof typeof ContentStatusConfig]
    : null;
  const isActive = game?.status === EContentStatus.ACTIVE;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      isHeader
      heading={
        game?.title || formatText('report-analytics.games.details.title')
      }
      position="right"
      className="max-w-150"
      isFooter
      confirmButtonProps={{
        label: formatText('common.actions.done'),
        onClick: () => setIsOpen(false),
        className: 'bg-purple-600 hover:bg-purple-700 w-24 ms-auto',
      }}
    >
      <div className="space-y-8 bg-white w-full">
        {/* Overview Section */}
        <div>
          <h3 className="text-lg font-bold text-neutral-primary">
            {formatText('report-analytics.games.details.overview')}
          </h3>
          <p className="text-neutral-secondary text-sm leading-6 mt-4">
            {game?.description}
          </p>

          <div className="grid grid-cols-2 gap-6 mt-10">
            <div className="flex items-start gap-3">
              <div className="text-neutral-hover">
                <IconStar size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary">
                  {formatText('report-analytics.games.details.level')}
                </span>
                <span className="text-base font-semibold text-neutral-primary capitalize">
                  {stageConfig?.label ? formatText(stageConfig?.label) : '-'}
                </span>
              </div>
            </div>

            {/* Status toggle */}

            <div className="flex flex-col min-w-0">
              <span className="text-sm text-neutral-secondary leading-5">
                {formatText('contentManagement.modal.status')}
              </span>
              <div className="flex gap-2">
                <Switch checked={isActive} onChange={() => {}} disabled />
                <span className="text-neutral-primary">
                  {statusConfig
                    ? formatText(statusConfig?.label)
                    : isActive
                      ? formatText('common.status.active')
                      : formatText('common.status.inactive')}
                </span>
              </div>
            </div>

            {/* Quarter */}
            <div className="flex items-start gap-3">
              <div className="text-neutral-hover">
                <IconCategory size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary">
                  {formatText('report-analytics.games.details.quarter')}
                </span>
                <span className="text-base font-semibold text-neutral-primary">
                  {game?.quarters ?? '-'}
                </span>
              </div>
            </div>

            {/* Round */}
            <div className="flex items-start gap-3">
              <div className="text-neutral-hover">
                <IconHash size={20} />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-neutral-secondary">
                  {formatText('report-analytics.games.details.round')}
                </span>
                <span className="text-base font-semibold text-neutral-primary">
                  {game?.rounds ?? '-'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-neutral-primary">
            {formatText('report-analytics.games.details.preview')}
          </h3>
          {game?.preview_image ? (
            <div className="w-full max-h-75! aspect-video rounded-2xl overflow-hidden">
              <img
                src={game?.preview_image}
                alt={game?.title}
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          ) : (
            <p className="text-sm text-neutral-secondary text-center px-4">
              {formatText('report-analytics.games.details.no-preview')}
            </p>
          )}
        </div>

        {/* Analytics Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-neutral-primary">
            {formatText('report-analytics.games.details.analytics')}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-warning-light-50 px-4 py-2 rounded-lg flex flex-col items-center justify-center gap-2">
              <span className="text-xs font-bold text-warning-secondary text-center">
                {formatText('report-analytics.games.details.avg-play-time')}
              </span>
              <span className="text-2xl font-bold text-orange-950">
                {game?.avg_play_time !== undefined
                  ? formatSecondsToDuration(game?.avg_play_time)
                  : '-'}
              </span>
            </div>
            <div className="bg-success-light-50 px-4 py-2 rounded-lg flex flex-col items-center justify-center gap-2">
              <span className="text-xs font-bold text-success-secondary text-center">
                {formatText('report-analytics.games.details.completion-rate')}
              </span>
              <span className="text-2xl font-bold text-success-900">
                {game?.completion_rate !== undefined
                  ? `${game?.completion_rate}%`
                  : '-'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </CustomModal>
  );
};

export default GameDetailsModal;
